import { IClinicToClinicTransferFilterBody, InventoryItem } from "@/api_framework/api_modals/Inventory";
import FormCheckbox from "@/app/components/ui/CheckBoxes/FormCheckbox";
import Input from "@/app/components/ui/Inputs/Input";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useOrderTransferBrandsListAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useOrderTransferBrandsListAPI";
import { useOrderTransferInventoryListAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useOrderTransferInventoryListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { IInventoryState, setTransferInventoryItems } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { FieldError, useForm } from "react-hook-form";
import { RxCrossCircled } from "react-icons/rx";
import { useNavigate } from "react-router";
import ProductSearchByClinicId from "../../InventoryMovement/InventorySnapshot/ProductSearchByClinicId";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useSubCategoryListAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useSubCategoryListAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import clsx from "clsx";

export interface IGoodInventoryPageProps {
  senderClinic: { id: string; name: string };
  receiverClinic: { id: string; name: string };
  subcategory: Array<string>;
  brand: Array<string>;
  checkedItems: boolean[];
  selectedItems: InventoryItem[];
  transferQty: Array<number>;
  search_query: string;
  transfer_inventory_type: string;
  searchQuery: string;
  variant_category_name?: string;
  variant_category_id?: string;
  clinic_tams: string;
}

const types = [
  {
    id: "1",
    title: "TAMS",
  },
  {
    id: "2",
    title: "CLINIC",
  },
];

const TransferInventory = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { transferBrandList, transferInventoryItems, subCategoryList, toNumber } = useRouteInfo("INVENTORY")?.routeState
    ?.state as IInventoryState;
  const transferBrandOptions = useSelectMenuReducer(transferBrandList, "brand_name", "brand_id");
  const fetchBrandList = useOrderTransferBrandsListAPI();
  const { execute: orderTransferApi, data } = useOrderTransferInventoryListAPI();
  const [selectedItems, setSelectedItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const { NO_APPOINTMENTS } = useImageConstants();
  const { execute: fetchClinicList, clinicList } = useClinicTamsFilterAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinicList, "display_name", "id");
  const { execute: fetchSubCategory } = useSubCategoryListAPI();
  const transferSubCategoryOptions = useSelectMenuReducer(subCategoryList.data, "subcategory_name", "subcategory_id");
  const dispatch = useAppDispatch();
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const { setVisible: toSelectedItemModal } = useModalActions("TO_SELECTED_ITEM_MODAL");

  const formHook = useForm<IGoodInventoryPageProps>({
    mode: "onChange",
    defaultValues: { searchQuery: "", transferQty: [] },
  });

  useEffect(() => {
    if (toNumber?.transfer_order_number) {
      localStorage.setItem("transfer_order_number", toNumber?.transfer_order_number);
    }
  }, [toNumber]);

  const storedOrderNumber = localStorage.getItem("transfer_order_number");

  useEffect(() => {
    localStorage.removeItem("clinic_tams");
    localStorage.removeItem("senderClinic");
    formHook.resetField("clinic_tams");
    formHook.resetField("senderClinic");
  }, []);

  useEffect(() => {
    const selectedType = formHook.watch("clinic_tams");
    if (selectedType) {
      if (selectedType === "TAMS") {
        fetchClinicList("", "TAMS");
      } else if (selectedType === "CLINIC") {
        fetchClinicList("", "CLINIC");
      }
    }
  }, [formHook.watch("clinic_tams")]);

  useEffect(() => {
    if (!formHook.watch("senderClinic")?.id) {
      return;
    }
    const updateFiltersAndFetchData = () => {
      const filterBody: IClinicToClinicTransferFilterBody = {
        clinic_id: formHook.watch("senderClinic.id"),
        transfer_inventory_type: `${location?.pathname?.split("/")[3]?.split("-")[1]}_inventory`,
      };
      if (selectedVariantId) {
        filterBody.vetic_variant_id = selectedVariantId;
      }
      if (formHook.watch("brand")?.length > 0) {
        filterBody["brand_ids"] = formHook.watch("brand");
      }
      if (formHook.watch("subcategory")?.length > 0) {
        filterBody["sub_category_ids"] = formHook.watch("subcategory");
      }
      orderTransferApi(filterBody, "10", `${pageNumber}`);
    };
    updateFiltersAndFetchData();
  }, [
    formHook.watch("senderClinic.id"),
    formHook.watch("subcategory"),
    formHook.watch("brand"),
    formHook.watch("searchQuery"),
    pageNumber,
    selectedVariantId,
  ]);

  const handleToSelectedItemClick = () => {
    toSelectedItemModal();
    dispatch(
      setTransferInventoryItems({
        data: {
          selectedItems: selectedItems,
          brands: formHook.watch("brand"),
          subcategories: formHook.watch("subcategory"),
          senderClinic: formHook.watch("senderClinic"),
          searchQuery: formHook.watch("searchQuery"),
          type: location?.pathname?.split("/")[3]?.split("-")[1],
          warehouse: formHook.watch("clinic_tams"),
          receiverClinic: formHook.watch("receiverClinic"),
        },
      }),
    );
  };

  useEffect(() => {
    fetchSubCategory();
    fetchBrandList();
  }, []);

  useEffect(() => {
    if (transferInventoryItems?.senderClinic?.id?.length > 0 && !formHook.watch("senderClinic.id")) {
      formHook.setValue("senderClinic.id", transferInventoryItems?.senderClinic?.id);
    }
    if (transferInventoryItems?.brands?.length > 0 && formHook.watch("brand")?.length == 0) {
      formHook.setValue("brand", transferInventoryItems?.brands);
    }
    if (transferInventoryItems?.subcategories?.length > 0 && formHook.watch("subcategory")?.length == 0) {
      formHook.setValue("subcategory", transferInventoryItems?.subcategories);
    }
    if (transferInventoryItems?.selectedItems?.length > 0 && selectedItems?.length == 0) {
      setSelectedItems(transferInventoryItems?.selectedItems);
    }
  }, [transferInventoryItems]);

  return (
    <>
      <div className="bg-white px-4 py-3 rounded-md">
        <h2 className="font-semibold text-base">
          {location?.pathname?.split("/")[3]?.split("-")[1] == "good" ? "Good" : "Bad"} Inventory Transfer
        </h2>
        <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-20">
          <div className="flex flex-row items-center gap-1 mt-2 sm:mt-3">
            <div className="flex justify-start items-center gap-1 py-2.5 w-auto sm:w-[150px] shrink-0 whitespace-nowrap h-[38px]">
              <span className="font-normal text-black text-xs">Order Number</span>
            </div>
            <p>{toNumber?.transfer_order_number || storedOrderNumber}</p>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center gap-1 mt-2 lg:mt-3">
            <div className="flex justify-start items-center gap-1 py-1 lg:py-2.5 w-full lg:w-[150px] shrink-0 whitespace-nowrap h-auto lg:h-[38px]">
              <span className="font-normal text-black text-xs">Sender Clinic/TAMS</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "Clinic/Tams is required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinic_tams", item?.title);
                }
              }}
              fieldError={(formHook?.formState?.errors?.clinic_tams as FieldError) ?? null}
              register={formHook.register("clinic_tams", { required: true })}
              selectItems={types}
              placeholder="Select Type"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={types?.filter((oc) => oc.title === formHook.watch("clinic_tams"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full lg:w-[200px]"
            />
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-1.5 lg:gap-10 border-b-2 pb-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-1 mt-2 lg:mt-5">
            <div className="flex justify-start items-center gap-1 py-1 lg:py-2.5 w-full lg:w-[150px] shrink-0 whitespace-nowrap h-auto lg:h-[38px]">
              <span className="font-normal text-black text-xs">Sender Clinic</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("senderClinic.id", item?.id);
                  formHook.setValue("senderClinic.name", item?.title);
                }
              }}
              defaultSelected={clinicsOrgItem.find((c) => c?.id == formHook.watch("senderClinic.id"))}
              fieldError={formHook?.formState?.errors?.senderClinic}
              register={formHook.register("senderClinic")}
              selectItems={clinicsOrgItem}
              placeholder="Select Sender Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              className="text-gray-800"
              containerClassName="w-full lg:w-[300px]"
              disabled={!formHook.watch("clinic_tams") || formHook.watch("clinic_tams")?.length === 0}
            />
          </div>
          <div className="flex flex-col lg:flex-row lg:items-center gap-1 mt-2 lg:mt-5">
            <div className="flex justify-start items-center gap-1 p-1 lg:p-2.5 w-full lg:w-[150px] shrink-0 whitespace-nowrap h-auto lg:h-[38px]">
              <span className="font-normal text-black text-xs">Receiver Clinic</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("receiverClinic", { id: item?.id, name: item?.title });
                  formHook.clearErrors("receiverClinic");
                }
              }}
              fieldError={formHook?.formState?.errors?.receiverClinic}
              register={formHook.register("receiverClinic")}
              selectItems={clinicsOrgItem?.filter((c) => c?.id !== formHook.watch("senderClinic.id"))}
              placeholder="Select Receiver Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              className="text-gray-800"
              containerClassName="w-full lg:w-[300px]"
              disabled={
                !formHook.watch("clinic_tams") ||
                formHook.watch("clinic_tams")?.length === 0 ||
                !formHook.watch("senderClinic.id")
              }
            />
          </div>
        </div>
        <div className="flex flex-col">
          <div className="mt-5 w-full">
            <div className="flex flex-col sm:flex-row gap-4">
              <ProductSearchByClinicId
                clinicId={formHook.watch("senderClinic.id")}
                brandIds={formHook.watch("brand")}
                subCategoryIds={formHook.watch("subcategory")}
                containerClassName="mt-2 shadow-none"
                itemActiveClassName="bg-orange-500 text-white"
                inputContainerClassName="w-full"
                flexibleAdd={false}
                transferDirection="C2C"
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("searchQuery", item?.name);
                    setSelectedVariantId(item?.id);
                  } else {
                    formHook.setValue("searchQuery", "");
                    setSelectedVariantId(null);
                  }
                }}
                searchType="Lab"
              />
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                <div className="w-full sm:w-auto">
                  <SearchableMultiSelectMenu
                    withSelectAll
                    errorMessages={[]}
                    onSelectItem={(item) => {
                      if (item) {
                        const categoriesId = item?.map((i) => i?.id);
                        formHook.setValue("brand", categoriesId);
                      }
                    }}
                    fieldError={formHook?.formState?.errors?.brand}
                    register={formHook.register("brand")}
                    selectItems={transferBrandOptions}
                    placeholder="Select Brands"
                    showTooltips={false}
                    showTypedErrors={false}
                    showDropdownIcon={true}
                    defaultSelected={transferBrandOptions?.filter((c) => formHook.watch("brand")?.includes(c?.id))}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full sm:w-[150px]"
                  />
                </div>
                <div className="w-full sm:w-auto">
                  <SearchableMultiSelectMenu
                    withSelectAll
                    errorMessages={[]}
                    onSelectItem={(item) => {
                      if (item) {
                        const categoriesId = item?.map((i) => i?.id);
                        formHook.setValue("subcategory", categoriesId);
                      }
                    }}
                    fieldError={formHook?.formState?.errors?.subcategory}
                    register={formHook.register("subcategory")}
                    selectItems={transferSubCategoryOptions}
                    placeholder="Select Sub Category"
                    showTooltips={false}
                    showTypedErrors={false}
                    showDropdownIcon={true}
                    defaultSelected={transferSubCategoryOptions?.filter((c) =>
                      formHook.watch("subcategory")?.includes(c?.id),
                    )}
                    listBoxClassName="w-full"
                    className="text-gray-800"
                    containerClassName="w-full sm:w-[200px]"
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 mr-2 outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px]">
                <div className="bg-white rounded-lg">
                  <table className="w-full min-w-[900px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                    <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                      <tr>
                        <th
                          scope="col"
                          className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] sticky left-0 z-20 bg-[#F5F5F5]"
                        ></th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[220px]">
                          Product Name
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[110px]">
                          Lot
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[150px]">
                          Manufacturer
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[110px]">
                          Expiry
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[100px]">
                          MRP
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[100px]">
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody className="overflow-hidden rounded-xl w-full">
                      {data?.data?.length > 0 ? (
                        data?.data?.map((item, i) => {
                          const isFirst = i === 0;
                          const isLast = i === data.data.length - 1;
                          const isSingle = data.data.length === 1;

                          return (
                            <tr
                              key={i}
                              className={clsx(
                                "bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal left-0 sticky z-20 bg-white",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                <FormCheckbox
                                  colorHash="#22c55e"
                                  fieldError={formHook?.formState?.errors?.checkedItems}
                                  register={formHook.register(`checkedItems.${i}`)}
                                  defaultChecked={selectedItems?.some((s) => s?.unique_row_id === item?.unique_row_id)}
                                  errorMessages={[]}
                                  onCheckChange={(checked) => {
                                    const items = checked
                                      ? [...selectedItems, item]
                                      : selectedItems?.filter((s) => s?.unique_row_id !== item?.unique_row_id);
                                    setSelectedItems(items);
                                  }}
                                />
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p className="w-[235px] whitespace-normal break-words">{item?.product_name}</p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p>{item?.lot_number}</p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p>{item?.manufacturer_name}</p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p>{item?.expiry_date}</p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p>{item?.mrp}</p>
                              </td>
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                <p>{item?.quantity}</p>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={7}>
                            <div className="flex justify-center bg-white rounded-lg w-full h-full py-6">
                              <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="Please Select Clinic." />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-center mt-5">
                <PaginationComponent
                  className="pagination-bar"
                  currentPage={pageNumber}
                  totalCount={data?.total_items || 0}
                  pageSize={10}
                  onPageChange={(page) => setPageNumber(page)}
                />
              </div>
            </div>
          </div>
          <div className="hidden border-gray-300 border-l-2"></div>
          <div className="mt-8 w-full">
            <div className="">
              <p className="font-medium text-base">Selected Items</p>
            </div>
            <div className="mt-4 lg:mt-6 outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[450px] 2xl:max-h-[550px]">
                <div className="bg-white rounded-lg">
                  <table className="w-full min-w-[560px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                    <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
                      <tr>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[180px]">
                          Product Name
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[160px]">
                          Details
                        </th>
                        <th className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[110px]">
                          Transfer Qty
                        </th>
                        <th
                          scope="col"
                          className="py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] min-w-[60px]"
                        ></th>
                      </tr>
                    </thead>
                    <tbody className="overflow-hidden rounded-xl w-full">
                      {selectedItems?.length > 0 ? (
                        selectedItems?.map((item, i) => {
                          const isFirst = i === 0;
                          const isLast = i === selectedItems.length - 1;
                          const isSingle = selectedItems.length === 1;

                          return (
                            <tr
                              key={i}
                              className={clsx(
                                "bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                <p className="w-[235px] whitespace-normal break-words">{item?.product_name}</p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p className="whitespace-nowrap">Qty: {item?.quantity}</p>
                                <p className="whitespace-nowrap">Lot: {item?.lot_number}</p>
                                <p className="whitespace-nowrap">Exp Date: {item?.expiry_date}</p>
                                <p className="whitespace-nowrap">Manufacturer: {item?.manufacturer_name}</p>
                                <p>Mrp: {item?.mrp}</p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <Input
                                  type="number"
                                  errorMessages={[{ type: "required", message: "Required" }]}
                                  fieldError={formHook?.formState?.errors?.transferQty}
                                  register={formHook.register(`transferQty.${i}`, {
                                    required: "This field is required",
                                    validate: (value) => value > 0 || "Transfer quantity must be greater than 0",
                                  })}
                                  defaultValue={selectedItems[i]?.transferQty}
                                  min={1}
                                  max={selectedItems[i]?.quantity ?? 0}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const rawValue = e.target.value;
                                    const inputValue = rawValue === "" ? undefined : Number(rawValue);
                                    const availableQuantity = selectedItems[i]?.quantity ?? 0;
                                    if (inputValue === undefined) {
                                      formHook.setValue(`transferQty.${i}`, undefined);
                                      setSelectedItems((prev) => {
                                        const updatedItems = [...prev];
                                        updatedItems[i] = { ...updatedItems[i], transferQty: undefined };
                                        return updatedItems;
                                      });
                                      return;
                                    }
                                    if (inputValue < 0) {
                                      e.target.value = "";
                                      formHook.setValue(`transferQty.${i}`, undefined);
                                      formHook.setError(`transferQty.${i}`, {
                                        type: "manual",
                                        message: "Transfer quantity cannot be negative",
                                      });
                                      setSelectedItems((prev) => {
                                        const updatedItems = [...prev];
                                        updatedItems[i] = { ...updatedItems[i], transferQty: undefined };
                                        return updatedItems;
                                      });
                                      return;
                                    }
                                    if (inputValue > availableQuantity) {
                                      e.target.value = String(availableQuantity);
                                    }
                                    formHook.setValue(`transferQty.${i}`, +e.target.value);
                                    formHook.clearErrors(`transferQty.${i}`);
                                    setSelectedItems((prev) => {
                                      const updatedItems = [...prev];
                                      updatedItems[i] = { ...updatedItems[i], transferQty: +e.target.value };
                                      return updatedItems;
                                    });
                                    if (inputValue > availableQuantity) {
                                      formHook.setError(`transferQty.${i}`, {
                                        type: "manual",
                                        message: `Value cannot exceed ${availableQuantity}`,
                                      });
                                    }
                                  }}
                                  onWheel={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </td>
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal cursor-pointer",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                <RxCrossCircled
                                  size={22}
                                  color="red"
                                  onClick={() => {
                                    formHook.setValue(
                                      "transferQty",
                                      formHook.watch("transferQty").filter((_, id) => id !== i),
                                    );
                                    setSelectedItems((prev) => {
                                      const updatedItems = [...prev];
                                      updatedItems[i] = { ...updatedItems[i], transferQty: null };
                                      return updatedItems;
                                    });
                                    setSelectedItems((prev) => {
                                      return prev.filter((s) => s?.unique_row_id !== item?.unique_row_id);
                                    });
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={7}>
                            <div className="flex justify-center bg-white rounded-lg w-full h-full py-6">
                              <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="Please Select Items." />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-end items-center gap-2 lg:gap-3 bg-white p-3 rounded-md">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            localStorage.removeItem("clinic_tams");
            localStorage.removeItem("senderClinic");
            formHook?.reset();
            navigate(getRouteKey("TRANSFER_ORDERS", "url"));
          }}
          type="button"
          className={`${SECONDARY_BUTTON} mt-2 lg:mt-5 w-auto`}
        >
          Back
        </button>
        <button
          disabled={
            selectedItems?.length == 0 ||
            selectedItems?.some((s) => !s?.transferQty) ||
            !formHook.watch("receiverClinic.id")
          }
          onClick={() => {
            handleToSelectedItemClick();
          }}
          type="button"
          className={`${PRIMARY_BUTTON} mt-2 lg:mt-5 w-auto disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          Continue
        </button>
      </div>
    </>
  );
};

export default TransferInventory;
